import { useState, useMemo } from "react";
import { useSuperAdminDashboard, useSuperAdminClients } from "@apis";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Input, Select, Tag, Segmented } from "antd";
import {
  TeamOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import {
  SuperAdminDashboardParamsI,
  UpcomingExpirationI,
  RecentPaymentI,
  ClientDetailI,
} from "@interfaces";
import "../../css/superadmin.css";
import { useRouterStore } from "../../../../../libs/stores";

const PERIOD_OPTIONS = [
  { label: "Hoy", value: "today" },
  { label: "Semana", value: "week" },
  { label: "Mes", value: "month" },
];

const STATUS_COLOR: Record<string, string> = {
  STATUS_SUBSCRIPTION_ACTIVE: "#52c41a",
  STATUS_SUBSCRIPTION_ABOUT_TO_EXPIRE: "#faad14",
  STATUS_SUBSCRIPTION_EXPIRED: "#ff4d4f",
  STATUS_SUBSCRIPTION_SUSPENDED: "#fa8c16",
  STATUS_SUBSCRIPTION_CANCELLED: "#d9d9d9",
};

const PIE_COLORS = ["#52c41a", "#faad14", "#ff4d4f", "#fa8c16", "#d9d9d9"];

// ── KPI Card ──────────────────────────────────────────
const KpiCard = ({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
}) => (
  <div className="sad-kpi-card">
    <div className="sad-kpi-icon" style={{ background: `${color}18`, color }}>
      {icon}
    </div>
    <div className="sad-kpi-info">
      <span className="sad-kpi-value">{value}</span>
      <span className="sad-kpi-label">{label}</span>
      {sub && <span className="sad-kpi-sub">{sub}</span>}
    </div>
  </div>
);

// ── Componente principal ──────────────────────────────
const DashboardSuperAdminComponent = () => {
  const firstName = useRouterStore((state) => state.firstName);
  
  const [period, setPeriod] = useState<"today" | "week" | "month">("month");
  const [clientSearch, setClientSearch] = useState("");
  const [clientPage, setClientPage] = useState(1);

  const dashParams: SuperAdminDashboardParamsI = { period };

  const { dashboardQuery } = useSuperAdminDashboard(dashParams);
  const { clientsQuery } = useSuperAdminClients({
    page: clientPage,
    limit: 10,
    search: clientSearch,
  });

  const dash = dashboardQuery.data?.data;
  const clients = clientsQuery.data?.data;

  const kpis = dash?.kpis;

  return (
    <div className="sad-page">
      {/* ── Header ── */}
      <div className="sad-header">
        <div className="sad-header-left">
          <h2 className="sad-title">{`Bienvenido, ${firstName}`}</h2>
          <span className="sad-subtitle">Vista global de la plataforma</span>
        </div>
        <Segmented
          options={PERIOD_OPTIONS}
          value={period}
          onChange={(v) => setPeriod(v as any)}
          className="sad-period-selector"
        />
      </div>

      {/* ── KPIs ── */}
      <div className="sad-kpi-grid">
        <KpiCard
          icon={<TeamOutlined />}
          label="Clientes activos"
          value={kpis?.activeClients ?? "—"}
          color="#534ab7"
        />
        <KpiCard
          icon={<CheckCircleOutlined />}
          label="Suscripciones activas"
          value={kpis?.activeSubscriptions ?? "—"}
          color="#52c41a"
        />
        <KpiCard
          icon={<DollarOutlined />}
          label="Ingresos del mes"
          value={kpis ? `$ ${Number(kpis.monthlyRevenue).toFixed(2)}` : "—"}
          sub={`${kpis?.monthlyPayments ?? 0} pagos`}
          color="#1677ff"
        />
        <KpiCard
          icon={<WarningOutlined />}
          label="Por vencer (7 días)"
          value={kpis?.aboutToExpire ?? "—"}
          color="#faad14"
        />
        <KpiCard
          icon={<CloseCircleOutlined />}
          label="Vencidas hoy"
          value={kpis?.expiredToday ?? "—"}
          color="#ff4d4f"
        />
        <KpiCard
          icon={<CreditCardOutlined />}
          label="Pagos este mes"
          value={kpis?.monthlyPayments ?? "—"}
          color="#13c2c2"
        />
      </div>

      {/* ── Gráficas fila 1 ── */}
      <div className="sad-charts-row">
        {/* Ingresos por mes */}
        <div className="sad-chart-card sad-chart-wide">
          <p className="sad-chart-title">Ingresos últimos 6 meses</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={dash?.revenueByMonth ?? []}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#534ab7" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#534ab7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eefb" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v: number) => [`$ ${v.toFixed(2)}`, "Ingresos"]}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#534ab7"
                fill="url(#colorRevenue)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Donut — por estado */}
        <div className="sad-chart-card">
          <p className="sad-chart-title">Suscripciones por estado</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={dash?.subscriptionByStatus ?? []}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={3}
              >
                {(dash?.subscriptionByStatus ?? []).map(
                  (entry: any, i: number) => (
                    <Cell
                      key={entry.code}
                      fill={
                        STATUS_COLOR[entry.code] ??
                        PIE_COLORS[i % PIE_COLORS.length]
                      }
                    />
                  ),
                )}
              </Pie>
              <Tooltip formatter={(v: number, n: string) => [v, n]} />
              <Legend iconType="circle" iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Gráficas fila 2 ── */}
      <div className="sad-charts-row">
        {/* Top planes */}
        <div className="sad-chart-card">
          <p className="sad-chart-title">Top 5 planes</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dash?.topPlans ?? []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eefb" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis
                dataKey="plan"
                type="category"
                tick={{ fontSize: 11 }}
                width={90}
              />
              <Tooltip />
              <Bar
                dataKey="count"
                fill="#534ab7"
                radius={[0, 6, 6, 0]}
                name="Suscripciones"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Ingresos por plan */}
        <div className="sad-chart-card">
          <p className="sad-chart-title">Ingresos por plan</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dash?.topPlans ?? []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eefb" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis
                dataKey="plan"
                type="category"
                tick={{ fontSize: 11 }}
                width={90}
              />
              <Tooltip
                formatter={(v: number) => [`$ ${v.toFixed(2)}`, "Ingresos"]}
              />
              <Bar
                dataKey="revenue"
                fill="#13c2c2"
                radius={[0, 6, 6, 0]}
                name="Ingresos"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Tablas fila 3 ── */}
      <div className="sad-tables-row">
        {/* Por vencer */}
        <div className="sad-table-card">
          <p className="sad-chart-title">
            Próximas a vencer
            <span className="sad-table-badge">
              {dash?.upcomingExpirations?.length ?? 0}
            </span>
          </p>
          <div className="sad-table-wrapper">
            <table className="sad-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Plan</th>
                  <th>Vence</th>
                  <th>Días</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {(dash?.upcomingExpirations ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="sad-table-empty">
                      Sin registros
                    </td>
                  </tr>
                ) : (
                  (dash?.upcomingExpirations ?? []).map(
                    (item: UpcomingExpirationI) => (
                      <tr key={item.id}>
                        <td className="sad-table-bold">{item.client}</td>
                        <td>{item.plan}</td>
                        <td style={{ fontSize: "0.68rem" }}>
                          {item.expiresAt}
                        </td>
                        <td>
                          <span
                            className="sad-days-badge"
                            style={{
                              background:
                                item.daysLeft <= 2 ? "#fff1f0" : "#fffbe6",
                              color: item.daysLeft <= 2 ? "#cf1322" : "#d48806",
                            }}
                          >
                            {item.daysLeft}d
                          </span>
                        </td>
                        <td>
                          <Tag
                            color={
                              STATUS_COLOR[item.statusSubscription] ?? "default"
                            }
                            style={{ fontSize: "0.65rem" }}
                          >
                            {item.statusSubscription}
                          </Tag>
                        </td>
                      </tr>
                    ),
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Últimos pagos */}
        <div className="sad-table-card">
          <p className="sad-chart-title">Últimos pagos</p>
          <div className="sad-table-wrapper">
            <table className="sad-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Plan</th>
                  <th>Monto</th>
                  <th>Fecha</th>
                  <th>Método</th>
                </tr>
              </thead>
              <tbody>
                {(dash?.recentPayments ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="sad-table-empty">
                      Sin registros
                    </td>
                  </tr>
                ) : (
                  (dash?.recentPayments ?? []).map((item: RecentPaymentI) => (
                    <tr key={item.id}>
                      <td className="sad-table-bold">{item.client}</td>
                      <td>{item.plan}</td>
                      <td
                        style={{
                          fontWeight: 600,
                          color: "#534ab7",
                          fontSize: "0.68rem",
                        }}
                      >
                        $ {Number(item.amount).toFixed(2)}
                      </td>
                      <td style={{ fontSize: "0.68rem" }}>
                        {item.paymentDate}
                      </td>
                      <td style={{ fontSize: "0.68rem" }}>
                        {item.paymentMethod}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Tabla clientes con detalle ── */}
      <div className="sad-clients-card">
        <div className="sad-clients-header">
          <p className="sad-chart-title">Detalle por cliente</p>
          <Input.Search
            placeholder="Buscar cliente..."
            allowClear
            size="small"
            style={{ width: 220 }}
            onSearch={(v) => {
              setClientSearch(v);
              setClientPage(1);
            }}
            onChange={(e) => {
              if (!e.target.value) {
                setClientSearch("");
                setClientPage(1);
              }
            }}
          />
        </div>
        <div className="sad-table-wrapper">
          <table className="sad-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Plan activo</th>
                <th>Vence</th>
                <th>Estado</th>
                <th>Suscripciones</th>
                <th>Total pagado</th>
              </tr>
            </thead>
            <tbody>
              {clientsQuery.isLoading ? (
                <tr>
                  <td colSpan={6} className="sad-table-empty">
                    Cargando...
                  </td>
                </tr>
              ) : (clients?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="sad-table-empty">
                    Sin registros
                  </td>
                </tr>
              ) : (
                (clients?.data ?? []).map((item: ClientDetailI) => (
                  <tr key={item.id}>
                    <td className="sad-table-bold">{item.name}</td>
                    <td style={{ fontSize: "0.68rem" }}>{item.plan ?? "—"}</td>
                    <td style={{ fontSize: "0.68rem" }}>{item.expiresAt}</td>
                    <td>
                      <Tag
                        color={STATUS_COLOR[item.statusCode ?? ""] ?? "default"}
                        style={{ fontSize: "0.65rem" }}
                      >
                        {item.statusSubscription}
                      </Tag>
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        fontSize: "0.68rem",
                        fontWeight: 600,
                      }}
                    >
                      {item.subscriptionCount}
                    </td>
                    <td
                      style={{
                        fontWeight: 600,
                        color: "#534ab7",
                        fontSize: "0.68rem",
                      }}
                    >
                      $ {Number(item.totalPaid).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Paginación simple */}
          {(clients?.total ?? 0) > 10 && (
            <div className="sad-pagination">
              <button
                className="sad-page-btn"
                disabled={clientPage === 1}
                onClick={() => setClientPage((p) => p - 1)}
              >
                ← Anterior
              </button>
              <span className="sad-page-info">
                Página {clientPage} de {Math.ceil((clients?.total ?? 0) / 10)}
              </span>
              <button
                className="sad-page-btn"
                disabled={clientPage >= Math.ceil((clients?.total ?? 0) / 10)}
                onClick={() => setClientPage((p) => p + 1)}
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardSuperAdminComponent;
